import { getAuth } from 'firebase/auth';
import {
	arrayUnion,
	doc,
	getDoc,
	getFirestore,
	increment,
	setDoc,
} from 'firebase/firestore';
import React from 'react';
import { rejectedAnswers } from '../../../firebase_functions/rejectedAnswers';
import {
	easyThreshold,
	failXTime,
	logEvent,
	numberOfAnswers,
	numberOfEasyReveals,
	numberOfGuesses,
	numberOfRounds,
} from './Constants';
import GameLayout from './GameLayout';

export type Round = { query: string; score: number; round: number };

export type Categories = {
	[key: string]: { queries?: string[]; order: number; hidden?: boolean };
};

export type LoadQuery = {
	tempCategory?: string;
	tempQuery?: string;
};

export type QOTD = {
	averageScore: number;
	count: number;
	query: string;
};

export const Game = ({ staticQuery = '' }: { staticQuery?: string }) => {
	const firestore = getFirestore();
	const playedQuestions: { [key: string]: string[] } = {};

	const user = getAuth().currentUser;
	const [categories, setCategories] = React.useState<Categories>({
		Animals: { order: 5 },
		Culture: { order: 1 },
		Entertainment: { order: 6 },
		Food: { order: 7 },
		Names: { order: 3 },
		People: { order: 2 },
		Questions: { order: 4 },
	});
	const [answers, setAnswers] = React.useState<{ [key: string]: string[] }>();
	const [questionOfTheDay, setQuestionOfTheDay] = React.useState<QOTD>();
	const [highScore, setHighScore] = React.useState(0);
	const [averageScores, setAverageScores] = React.useState();

	const numberOfGuessesOrStatic = staticQuery ? Infinity : numberOfGuesses;
	const processGuess = (guess: string) => {
		guess = guess.toLowerCase().trim();
		guess = guess.replace(/[^\w\s]/g, ''); // remove anything but letters, numbers, space
		guess = guess.replace(/(.)(?=\1)/g, ''); // remove any repeated character

		rejectedAnswers.forEach((suffix) => {
			if (guess.substring(guess.length - suffix.length - 1) === ' ' + suffix) {
				guess = guess.substring(0, guess.length - suffix.length);
			}
		});

		rejectedAnswers.forEach((prefix) => {
			if (guess.substring(0, prefix.length + 1) === prefix + ' ') {
				guess = guess.substring(prefix.length + 1);
			}
		});

		guess.replace(/mister/g, 'mr');
		guess.replace(/miss/g, 'ms');
		guess.replace(/misses/g, 'mrs');
		guess.replace(/doctor/g, 'dr');

		return guess;
	};

	const [round, setRound] = React.useState<number>(1);
	const [wrong, setWrong] = React.useState<number>(0);
	const [score, setScore] = React.useState<number>(0);
	const [category, setCategory] = React.useState<string>('');
	const [query, setQuery] = React.useState<string>(staticQuery);

	const [numberOfRoundsOrQOTD, setNumberOfRoundsOrQOTD] = React.useState(
		staticQuery ? 1 : numberOfRounds
	);
	const [loadingCategory, setLoadingCategory] = React.useState<string>('');
	const [response, setResponse] = React.useState<string>('');
	const [correctAnswers, setCorrectAnswers] = React.useState<boolean[]>([]);

	const [processedAnswers, setProcessedAnswers] = React.useState(
		staticQuery && answers
			? answers[staticQuery].map((answer) => processGuess(answer))
			: []
	);
	const [giveUp, setGiveUp] = React.useState(false);
	const [roundSummary, setRoundSummary] = React.useState<Round[]>([]);
	const [showResults, setShowResults] = React.useState(false);

	// Stateful functions

	const updateRounds = () => {
		setRoundSummary((existingRounds: Round[]) =>
			existingRounds.concat([
				{
					query,
					round,
					score: roundSummary.reduce((prev, curr) => prev - curr.score, score),
				},
			])
		);
	};

	const loadQuery = ({ tempCategory = '', tempQuery = '' }: LoadQuery) => {
		if (!answers) {
			// Firebase queries haven't loaded yet
			return tempCategory && setLoadingCategory(tempCategory);
		}

		let newCategory = tempCategory;
		let newQuery = tempQuery;

		if (!category) {
			setCategory(newCategory);
			setNumberOfRoundsOrQOTD(
				newCategory === 'Question of the Day' ? 1 : numberOfRounds
			);
		}

		logEvent(window.gtag, 'loadQuery', { category });

		if (tempCategory === 'Easy') {
			setCorrectAnswers(
				new Array(numberOfAnswers - numberOfEasyReveals)
					.fill(false)
					.concat(new Array(numberOfEasyReveals).fill(true))
			);
		}

		if (newQuery === '') {
			const categoryNames: string[] = Object.keys(categories);
			while (
				newCategory === 'Random' ||
				newCategory === 'Easy' ||
				categories[newCategory].hidden
			) {
				newCategory =
					categoryNames[Math.floor(Math.random() * categoryNames.length)];
			}

			if (newCategory in playedQuestions === false) {
				playedQuestions[newCategory] = [];
			}

			while (
				!newQuery ||
				playedQuestions[newCategory].indexOf(newQuery) > -1 ||
				(tempCategory === 'Easy' &&
					averageScores &&
					averageScores[newQuery] < easyThreshold)
			) {
				const tempQueries = categories[newCategory].queries || '';
				newQuery = tempQueries
					? tempQueries[
							Math.floor(
								(categories[newCategory]?.queries?.length ?? 0) * Math.random()
							)
						]
					: '';
			}
		}
		setQuery(newQuery);
		setProcessedAnswers(() => {
			return answers[newQuery].map((answer) => processGuess(answer));
		});

		if (newCategory !== 'Question of the Day' && tempCategory !== 'Easy') {
			if (
				newCategory in playedQuestions &&
				playedQuestions[newCategory].length + 1 ===
					categories[newCategory].queries?.length
			) {
				playedQuestions[newCategory] = [];
			} else {
				playedQuestions[newCategory].push(newQuery);
			}
		}
		return refocusInput('mainInput');
	};

	const submitAnswer = () => {
		if (response.trim().length === 0) {
			return document?.getElementById('mainInput')?.focus();
		}
		const winConditions = (guess: string, answer: string) => {
			const minimumWordLength = 4;

			if (guess === answer) {
				return true;
			}

			if (guess.slice(0, -1) === answer || guess === answer.slice(0, -1)) {
				return true;
			}

			const spacelessGuess = guess.replace(/\W/g, '');
			const spacelessAnswer = answer.replace(/\W/g, '');
			if (
				spacelessGuess.length >= minimumWordLength &&
				spacelessAnswer.indexOf(spacelessGuess) > -1
			) {
				return true;
			}

			if (
				guess
					.split(' ')
					.reduce(
						(acc, word) =>
							word.length > minimumWordLength &&
							(answer.indexOf(word) > -1 || spacelessAnswer.indexOf(word) > -1)
								? true
								: acc,
						false
					)
			) {
				return true;
			}
			return false;
		};

		const processedGuess = processGuess(response);
		let points = 0;
		processedAnswers.forEach(
			(answer, index) =>
				!correctAnswers[index] &&
				winConditions(processedGuess, answer) &&
				(correctAnswers[index] = true) &&
				(points += 1000 * (numberOfAnswers - index))
		);
		let newWrong = wrong;
		if (points === 0) {
			newWrong++;
		} else if (
			correctAnswers.filter((val) => val).length === processedAnswers.length
		) {
			setWrong(numberOfGuessesOrStatic);
			setGiveUp(true);
		}
		setResponse('');
		if (newWrong === numberOfGuessesOrStatic) {
			updateRounds();
		}
		setWrong(newWrong);
		setScore(score + points);
	};

	const giveUpFunction = () => {
		setGiveUp(true);
		setWrong(numberOfGuessesOrStatic);
		updateRounds();
	};

	const nextRound = (tempCategory: string) => {
		setShowResults(false);
		setGiveUp(false);
		setResponse('');
		setWrong(0);
		setCorrectAnswers([]);
		if (round < numberOfRoundsOrQOTD) {
			refocusInput('mainInput');
			setRound(round + 1);
			loadQuery({
				tempCategory: tempCategory || category,
			});
		} else {
			setRound(1);
			setScore(0);
			setCategory('');
			setQuery('');
			setRoundSummary([]);
			if (score > highScore && setHighScore) {
				setHighScore(score);
			}
		}
	};

	const shareText = () =>
		wrong === numberOfGuessesOrStatic && answers
			? answers[query].reduce(
					(acc, answer, index) =>
						`${acc}\n${(correctAnswers[index] ? '🟦' : '⬜').repeat(
							Math.floor(answer.length * 0.66)
						)}`,
					`Google Feud - ${query}\n`
				) + `\n\n${score.toLocaleString()} points`
			: '';

	const refocusInput = (whichInput: string) => {
		if (document.getElementById(whichInput)) {
			document.getElementById(whichInput)?.blur();
			setTimeout(() => {
				if (document.getElementById(whichInput)) {
					document.getElementById(whichInput)?.focus();
				}
			}, failXTime);
		}
	};

	React.useEffect(() => {
		const loadQuestionsAsync = async () => {
			const Queries = await getDoc(doc(firestore, 'GameData/Queries'));
			if (!Queries?.exists()) {
				return;
			}
			const data = Queries.data();
			if (!data) {
				return;
			}
			setQuestionOfTheDay(data.qotd);
			setAnswers(data.AllAnswers);
			setCategories(data.Categories);
			setAverageScores(data.AverageScores);
		};
		loadQuestionsAsync();
	}, [firestore]);

	React.useEffect(() => {
		const loadHighScoreAsync = async () => {
			if (user === null) {
				return;
			}
			const HighScoreSnapshot = await getDoc(
				doc(firestore, `UserData/${user.uid}`)
			);
			if (HighScoreSnapshot.exists()) {
				const data = HighScoreSnapshot.data();
				if (typeof data !== 'undefined') {
					setHighScore(data.highScore ? data.highScore.score : 0);
				}
			}
		};
		loadHighScoreAsync();
	}, [firestore, user]);

	React.useEffect(() => {
		if (round !== numberOfRoundsOrQOTD || !user) {
			return;
		}
		const newScore = roundSummary.reduce((acc, val) => acc + val.score, 0);
		setDoc(
			doc(firestore, `UserData/${user.uid}`),
			{
				allTimeScore: increment(newScore),
				lastQuery: query,
				lastQueryScore: newScore,
				questionsPlayed: increment(numberOfRoundsOrQOTD),
				recentlyPlayed: arrayUnion(...roundSummary.map((val) => val.query)),
				...(newScore > highScore && {
					highScore: { category, score: newScore },
				}),
			},
			{ merge: true }
		);
	}, [
		round,
		numberOfRoundsOrQOTD,
		roundSummary,
		user,
		firestore,
		query,
		highScore,
		category,
	]);

	return (
		<GameLayout
			answers={answers}
			categories={categories}
			category={category}
			correctAnswers={correctAnswers}
			giveUp={giveUp}
			giveUpFunction={giveUpFunction}
			highScore={highScore}
			loadingCategory={loadingCategory}
			loadQuery={loadQuery}
			nextRound={nextRound}
			numberOfGuessesOrStatic={numberOfGuessesOrStatic}
			numberOfRoundsOrQOTD={numberOfRoundsOrQOTD}
			processedAnswers={processedAnswers}
			query={query}
			questionOfTheDay={questionOfTheDay}
			response={response}
			round={round}
			roundSummary={roundSummary}
			score={score}
			setLoadingCategory={setLoadingCategory}
			setResponse={setResponse}
			setShowResults={setShowResults}
			shareText={shareText}
			showResults={showResults}
			submitAnswer={submitAnswer}
			useStaticQuery={staticQuery}
			wrong={wrong}
		/>
	);
};
